import { IconX } from "@tabler/icons-react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useLanguage } from "../../../context/LanguageContext";
import {
  useFloorPalletsCreateMutation,
  useFloorPalletsUpdateMutation,
  useFetchShelfAreaFloors,
  useFetchShelfRowWhRows,
} from "../../../hooks/shelveHooks";
import { ShelfRowFloorWithPallets } from "../../../apis/shelveApis";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

interface PalletFormData {
  palletName: string;
  numberOfPallets: string;
  warehouseId: string;
  shelveId: string;
  rackInstanceId: string;
  clientId: string;
  rackId: string;
  whRowId?: string;
}

interface CreatePalletModalProps {
  open: boolean;
  onClose: () => void;
  warehouseId: string;
  shelveId?: string;
  rowInstanceId?: string; // From parent context
  clientId: string;
  rowId?: string; // From parent context
  fetchShelves: () => void;
  // Floor-specific props
  floorId?: string;
  floorName?: string;
  areaId?: string;
}

export default function CreatePalletModal({
  open,
  onClose,
  warehouseId,
  shelveId,
  rowInstanceId,
  clientId,
  rowId,
  fetchShelves,
  floorId,
  floorName,
  areaId,
}: CreatePalletModalProps) {
  const { t } = useLanguage();
  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<PalletFormData>({
    defaultValues: {
      palletName: "P",
      numberOfPallets: "1",
      warehouseId,
      shelveId,
      rackInstanceId: rowInstanceId,
      clientId,
      rackId: rowId,
    },
  });

  const { mutate: createFloorPallet, isPending } =
    useFloorPalletsCreateMutation();
  const { mutate: updateFloorPallet, isPending: isUpdating } =
    useFloorPalletsUpdateMutation();
  const queryClient = useQueryClient();

  // Fetch existing floors with pallets to check if floor already has pallets
  const { data: floorsData } = useFetchShelfAreaFloors(areaId || "");

  // Fetch WhRows for rack creation
  const { data: whRowsData } = useFetchShelfRowWhRows(areaId || "");

  console.log("Floors data from API:", floorsData);
  console.log("Looking for floorId:", floorId);

  // Check if the current floor already has pallets
  const currentFloorPallets =
    floorsData?.find((floor: ShelfRowFloorWithPallets) => floor.id === floorId)
      ?.pallet || [];
  console.log("Current floor pallets:", currentFloorPallets);
  const hasExistingPallets = currentFloorPallets.length > 0;

  const watchedPrefix = watch("palletName");
  const watchedCount = watch("numberOfPallets");
  const [preview, setPreview] = useState<string[]>([]);

  // Update preview IDs whenever name or count changes
  useEffect(() => {
    const count = parseInt(watchedCount) || 0;
    const prefix = watchedPrefix || "P";
    const generated = Array.from(
      { length: count },
      (_, i) => `${prefix}-${i + 1}`,
    );
    setPreview(generated);
  }, [watchedPrefix, watchedCount]);

  const onSubmit = (data: PalletFormData) => {
    // Create payload for floor pallets API matching backend logic
    const count = parseInt(data.numberOfPallets) || 1;

    const payload = {
      whAreaId: areaId,
      whFloorId: floorId,
      pallets: [
        {
          palletName: data.palletName,
          palletNumbers: count,
        },
      ],
    };

    // Choose appropriate mutation based on existing pallets
    const mutation = hasExistingPallets ? updateFloorPallet : createFloorPallet;
    const action = hasExistingPallets ? "updated" : "added";

    mutation(payload as any, {
      onSuccess: () => {
        if (action === "updated") {
          toast.success(t("Pallet(s) updated successfully"));
        } else {
          toast.success(t("Pallet(s) added successfully"));
        }
        reset();
        onClose();
        // Invalidate shelf area floors query to refresh UI
        queryClient.invalidateQueries({ queryKey: ["shelfAreaFloors"] });
        queryClient.invalidateQueries({ queryKey: ["floorPallets"] });
        fetchShelves();
      },
      onError: (error: any) => {
        if (action === "updated") {
          toast.error(`${t("Update failed")}: ${error.message || error}`);
        } else {
          toast.error(`${t("Creation failed")}: ${error.message || error}`);
        }
      },
    });
  };

  if (!open) return null;

  return (
    // <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
    <div className="bg-[#F7F6F2] rounded-3xl  flex flex-col overflow-hidden shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b bg-white">
        <h2 className="text-lg font-bold text-[#212121]">
          {t("Add Pallet Locations")}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-full border border-gray-300 hover:bg-gray-100 transition"
        >
          <IconX size={18} />
        </button>
      </div>

      {/* Body */}
      <form
        id="pallet-form"
        onSubmit={handleSubmit(onSubmit)}
        className="p-6 space-y-6"
      >
        <div className="grid grid-cols-2 gap-4">
          {/* Pallet Name Prefix */}
          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              {t("Pallet Prefix")}
            </label>
            <Controller
              name="palletName"
              control={control}
              rules={{
                required: t("Required (e.g., P)"),
                maxLength: { value: 3, message: t("Max 3 characters") },
              }}
              render={({ field }) => (
                <input
                  {...field}
                  placeholder={t("e.g. P")}
                  maxLength={3}
                  className="w-full rounded-xl py-2.5 px-4 outline-none border border-gray-300 focus:ring-2 focus:ring-yellow-400 bg-white transition-all"
                />
              )}
            />
            {errors.palletName && (
              <p className="text-red-500 text-[10px] mt-1">
                {t(errors.palletName.message || "")}
              </p>
            )}
          </div>

          {/* Total Count */}
          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              {t("Quantity")}
            </label>
            <Controller
              name="numberOfPallets"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  className="w-full rounded-xl py-2.5 px-4 outline-none border border-gray-300 focus:ring-2 focus:ring-yellow-400 bg-white transition-all"
                >
                  {[1, 2, 3, 4, 5, 10, 20].map((num) => (
                    <option key={num} value={num}>
                      {t(`${num} Pallets`)}
                    </option>
                  ))}
                </select>
              )}
            />
          </div>
        </div>

        {/* Real-life Preview Area */}
        <div className="bg-white/60 border border-dashed border-gray-300 rounded-2xl p-4">
          <label className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">
            {t("Preview Location Labels")}
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            {preview.map((id, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-mono font-bold rounded-lg border border-yellow-200"
              >
                {id}
              </span>
            ))}
          </div>
        </div>
      </form>

      {/* Footer */}
      <div className="flex justify-end p-4 bg-gray-50 border-t">
        <button
          form="pallet-form"
          disabled={isPending || isUpdating}
          type="submit"
          className="px-8 py-2.5 rounded-full bg-[#FBD037] text-black font-bold hover:bg-yellow-500 shadow-md active:scale-95 transition-all disabled:opacity-50"
        >
          {isPending || isUpdating
            ? hasExistingPallets
              ? t("Updating...")
              : t("Creating...")
            : t("Confirm & Add")}
        </button>
      </div>
    </div>
    // </div>
  );
}
